import { useState, useEffect, useCallback, Component, type ReactNode } from 'react';
import { useAuthStore } from './store/authStore';

export class ErrorBoundary extends Component<{ children: ReactNode }, { error: string | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { error: error.message };
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 32, fontFamily: 'monospace', background: '#fff', color: '#c00' }}>
          <strong>App-Fehler:</strong>
          <pre style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>{this.state.error}</pre>
          <button onClick={() => { localStorage.clear(); window.location.reload(); }}
            style={{ marginTop: 16, padding: '8px 16px', background: '#c00', color: '#fff', border: 'none', cursor: 'pointer' }}>
            Cache leeren &amp; neu laden
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
import AuthView from './views/AuthView';
import Sidebar from './components/Sidebar';
import AppBar from './components/AppBar';
import ProjectsView from './views/ProjectsView';
import CalendarView from './views/CalendarView';
import TodosView from './views/TodosView';
import NotesView from './views/NotesView';
import EditorView from './views/EditorView';
import SettingsView from './views/SettingsView';
import type { WorksheetData } from './types';

// ── localStorage helpers ──────────────────────────────────────
const LS_KEY = 'sketchlessons_worksheets';

function loadSheets(): WorksheetData[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveSheets(sheets: WorksheetData[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(sheets));
}

export function getSheet(id: string): WorksheetData | undefined {
  return loadSheets().find(s => s.id === id);
}

export function upsertSheet(sheet: WorksheetData) {
  const sheets = loadSheets().filter(s => s.id !== sheet.id);
  sheets.unshift(sheet);
  saveSheets(sheets);
}

export function deleteSheet(id: string) {
  saveSheets(loadSheets().filter(s => s.id !== id));
}

// Editor context
interface EditorData {
  id?: string;
  title: string;
  content?: string;
  folderId?: string;
  subject?: string;
  grade?: string;
}

export default function App() {
  const { isAuthenticated, initializeAuth } = useAuthStore();
  const [currentView, setCurrentView] = useState('projects');
  const [editorData, setEditorData] = useState<EditorData | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // useCallback muss vor allen bedingten Returns stehen (Rules of Hooks)
  const handleEditorSaved = useCallback(() => {
    setEditorData(null);
    setCurrentView('projects');
  }, []);

  useEffect(() => {
    initializeAuth();
    setIsReady(true);
  }, [initializeAuth]);

  if (!isReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        <img src="/brand/wordmark.svg" alt="SketchLessons" className="h-9 animate-pulse" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AuthView />;
  }

  const handleNavigate = (view: string) => {
    if (view !== 'editor') setEditorData(null);
    setCurrentView(view);
    setSidebarOpen(false);
  };

  const handleNewWorksheet = () => {
    // Arbeitsblätter gehören in einen Ordner — daher zur Projektansicht führen.
    setEditorData(null);
    setCurrentView('projects');
    setSidebarOpen(false);
  };

  const handleOpenEditor = (data: EditorData) => {
    setEditorData(data);
    setCurrentView('editor');
  };

  const handleEditorBack = () => {
    setEditorData(null);
    setCurrentView('projects');
  };

  const viewMeta: Record<string, { title: string; subtitle?: string }> = {
    projects: { title: 'Arbeitsblätter', subtitle: 'Organisiere deine Unterrichtsmaterialien' },
    calendar: { title: 'Kalender', subtitle: 'Termine und Stundenplan' },
    todos: { title: 'Todo-Liste', subtitle: 'Deine Aufgaben für heute' },
    notes: { title: 'Notizen', subtitle: 'Gedanken und Ideen' },
    editor: { title: editorData?.title || 'Neues Arbeitsblatt', subtitle: 'Arbeitsblatt erstellen und bearbeiten' },
    settings: { title: 'Einstellungen', subtitle: 'Profil & Voreinstellungen' },
  };

  const meta = viewMeta[currentView] || viewMeta.projects;

  return (
    <div className="h-screen flex bg-paper">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-20 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar — hidden on mobile unless open */}
      <div className={`
        fixed inset-y-0 left-0 z-30 flex md:static md:flex-shrink-0
        transition-transform duration-200 shadow-2xl md:shadow-none
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        <Sidebar
          currentView={currentView}
          onNavigate={handleNavigate}
          onNewWorksheet={handleNewWorksheet}
        />
      </div>

      <div className="flex-1 min-w-0 flex flex-col">
        <AppBar
          title={meta.title}
          subtitle={meta.subtitle}
          onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
        />
        <main className="flex-1 min-h-0 overflow-auto p-7">
          {currentView === 'projects' && <ProjectsView onOpenEditor={handleOpenEditor} />}
          {currentView === 'calendar' && <CalendarView />}
          {currentView === 'todos' && <TodosView />}
          {currentView === 'notes' && <NotesView />}
          {currentView === 'settings' && <SettingsView />}
          {currentView === 'editor' && (
            <EditorView
              sheetId={editorData?.id || undefined}
              folderId={editorData?.folderId}
              initialTitle={editorData?.title}
              initialSubject={editorData?.subject}
              initialGrade={editorData?.grade}
              onSaved={handleEditorSaved}
              onBack={handleEditorBack}
            />
          )}
        </main>
      </div>
    </div>
  );
}
